import { lazyNamed } from "src/utils/lazy-named";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { IdrDataRoute } from "src/pages/identity-resolution/data-route";
import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { Threshold } from "src/types/idr";

const CreateIdentityResolutionGraph = lazyNamed(
  () => import("./create"),
  "CreateIdentityResolutionGraph",
);
const IdentityResolutionGraph = lazyNamed(
  () => import("./graph"),
  "IdentityResolutionGraph",
);
const IdentityResolutionGraphLoader = lazyNamed(
  () => import("./graph"),
  "IdentityResolutionGraphLoader",
);
const graphsImport = () => import("./graphs");
const IdentityResolutionGraphs = lazyNamed(
  graphsImport,
  "IdentityResolutionGraphs",
);

export const preloads: RoutePreload[] = [["/idr", graphsImport]];
const Summary = lazyNamed(() => import("./graph/summary/summary"), "Summary");
const GraphHealth = lazyNamed(() => import("./graph/health"), "GraphHealth");
const SuperclustersDrawer = lazyNamed(
  () => import("./graph/summary/superclusters/superclusters-drawer"),
  "SuperclustersDrawer",
);
const IdentifierDetails = lazyNamed(
  () =>
    import(
      "./graph/summary/superclusters/identifier-details/identifier-details"
    ),
  "IdentifierDetails",
);
const Configuration = lazyNamed(
  () => import("./graph/configuration"),
  "Configuration",
);
const CreateIdentityResolutionModel = lazyNamed(
  () => import("./graph/create-model"),
  "CreateIdentityResolutionModel",
);
const Rules = lazyNamed(() => import("./graph/rules"), "Rules");
const Runs = lazyNamed(() => import("./graph/runs"), "Runs");
const TrainingRuns = lazyNamed(
  () => import("./graph/training-runs"),
  "TrainingRuns",
);
const Model = lazyNamed(() => import("./graph/model"), "Model");
const GoldenRecordColumn = lazyNamed(
  () => import("./graph/golden-record-column"),
  "GoldenRecordColumn",
);
const Models = lazyNamed(() => import("./graph/models"), "Models");
const GoldenRecordRoute = lazyNamed(
  () => import("./graph/golden-record-context"),
  "GoldenRecordRoute",
);
const IdrProfiles = lazyNamed(() => import("./graph/profiles"), "IdrProfiles");
const MergeTraceability = lazyNamed(
  () => import("./graph/merge-traceability"),
  "MergeTraceability",
);
const IdrProfile = lazyNamed(
  () => import("./graph/profiles/profile"),
  "IdrProfile",
);
const Identity = lazyNamed(
  () => import("./graph/profiles/identity"),
  "Identity",
);
const IdentifierBreakdown = lazyNamed(
  () => import("./graph/profiles/identifier-breakdown"),
  "IdentifierBreakdown",
);
const ModelBreakdown = lazyNamed(
  () => import("./graph/profiles/model-breakdown"),
  "ModelBreakdown",
);
const ProfileAttributes = lazyNamed(
  () => import("./graph/profiles/profile-attributes"),
  "ProfileAttributes",
);
const ProfileGraphTab = lazyNamed(
  () => import("./graph/profiles/profile-graph/profile-graph-tab"),
  "ProfileGraphTab",
);
const Alerting = lazyNamed(() => import("./graph/alerting"), "Alerting");

export const routes: Array<RouteObject> = [
  {
    path: "idr",
    spacesAccess: "hidden-from-limited-view",
    element: <IdentityResolutionGraphs />,
  },
  {
    path: "idr/new",
    spacesAccess: "hidden-from-limited-view",
    element: <CreateIdentityResolutionGraph />,
  },
  {
    path: "idr/:id/*",
    spacesAccess: "hidden-from-limited-view",
    element: <IdentityResolutionGraphLoader />,
    children: [
      {
        spacesAccess: "hidden-from-limited-view",
        element: <IdentityResolutionGraph />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to={`summary${location.search}`} replace />,
          },
          {
            path: "summary",
            spacesAccess: "hidden-from-limited-view",
            element: <Summary />,
          },
          {
            path: "health",
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view",
                element: <Navigate to={Threshold.Exact} replace />,
              },
              {
                path: ":threshold",
                spacesAccess: "hidden-from-limited-view",
                element: <GraphHealth />,
              },
            ],
          },
          {
            path: "runs",
            spacesAccess: "hidden-from-limited-view",
            element: <Runs />,
            children: [
              {
                path: "superclusters/:runId",
                spacesAccess: "hidden-from-limited-view",
                element: (
                  <IdrDataRoute>
                    <SuperclustersDrawer />
                  </IdrDataRoute>
                ),
                children: [
                  {
                    path: "identifier",
                    spacesAccess: "hidden-from-limited-view",
                    element: <IdentifierDetails />,
                  },
                ],
              },
            ],
          },
          {
            path: "training-runs",
            spacesAccess: "hidden-from-limited-view",
            element: <TrainingRuns />,
          },
          {
            path: "models/*",
            spacesAccess: "hidden-from-limited-view",
            element: <Models />,
            children: [
              {
                path: ":modelId",
                spacesAccess: "hidden-from-limited-view",
                element: <Model />,
              },
              {
                path: "new",
                spacesAccess: "hidden-from-limited-view",
                element: <CreateIdentityResolutionModel />,
              },
            ],
          },
          {
            path: "rules",
            spacesAccess: "hidden-from-limited-view",
            element: <Rules />,
          },
          {
            path: "debug",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <FeatureFlagRoute flagKey="idrMergeTraceabilityFrontend">
                <IdrDataRoute>
                  <MergeTraceability />
                </IdrDataRoute>
              </FeatureFlagRoute>
            ),
          },
          {
            path: "alerting",
            spacesAccess: "hidden-from-limited-view",
            element: <Alerting />,
          },
          {
            path: "configuration",
            spacesAccess: "hidden-from-limited-view",
            element: <Configuration />,
          },
          {
            path: "profiles",
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view",
                element: <Navigate to={Threshold.Exact} replace />,
              },
              {
                path: ":threshold",
                spacesAccess: "hidden-from-limited-view",
                element: (
                  <IdrDataRoute>
                    <IdrProfiles />
                  </IdrDataRoute>
                ),
                children: [
                  {
                    path: ":profileId",
                    spacesAccess: "hidden-from-limited-view",
                    element: <IdrProfile />,
                    children: [
                      {
                        index: true,
                        spacesAccess: "hidden-from-limited-view",
                        element: <Navigate to="identity" replace />,
                      },
                      {
                        path: "identity",
                        spacesAccess: "hidden-from-limited-view",
                        element: <Identity />,
                        children: [
                          {
                            path: "identifier/:identifier",
                            spacesAccess: "hidden-from-limited-view",
                            element: <IdentifierBreakdown />,
                          },
                          {
                            path: "model/:modelSlug",
                            spacesAccess: "hidden-from-limited-view",
                            element: <ModelBreakdown />,
                          },
                        ],
                      },
                      {
                        path: "attributes",
                        spacesAccess: "hidden-from-limited-view",
                        element: <ProfileAttributes />,
                      },
                      {
                        path: "profile-graph",
                        spacesAccess: "hidden-from-limited-view",
                        element: <ProfileGraphTab />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "golden-record",
            spacesAccess: "hidden-from-limited-view",
            element: <GoldenRecordRoute />,
            children: [
              {
                path: ":columnName",
                spacesAccess: "hidden-from-limited-view",
                element: <GoldenRecordColumn />,
              },
            ],
          },
        ],
      },
    ],
  },
];
